/**
 * The login page.
 *
 * Two fields: documento (UNMASKED — any document type; normalised on submit)
 * and password.
 * "Entrar" submits. On success: navigate based on activation state. On failure:
 * uniform `invalid_credentials` — "document not found" and "wrong password" are
 * indistinguishable (ADR-0003). No self-signup, no forgot-password link.
 */
import { useCallback, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentoInput } from '../components/DocumentoInput.tsx';
import { PasswordInput } from '../components/PasswordInput.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { IconAlert } from '../components/icons.tsx';
import { LocaleSelector } from '../components/LocaleSelector.tsx';
import styles from './LoginPage.module.css';

export function LoginPage(): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();

  const [raw, setRaw] = useState('');
  const [canonical, setCanonical] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (submitting) return;

      setSubmitting(true);
      auth.clearError();

      const result = await auth.login(canonical, password);

      if (result.ok) {
        // Don't navigate — AuthProvider's navigation effect handles it when
        // the auth state updates. Navigating here races with the state update.
        setSubmitting(false);
        return;
      } else {
        setSubmitting(false);
      }
    },
    [canonical, password, auth, submitting],
  );

  return (
    <main className={styles['page']}>
      {/* Before the header, not inside it: a customer who cannot read the page
          needs to find this without parsing anything above it. */}
      <div className={styles['locale-bar']}>
        <LocaleSelector />
      </div>

      <header className={styles['header']}>
        <img className={styles['logo']} src="/brand/logo-international-horizontal.png" alt={t('brand.name')} />
        <h1 className={`ns-display ${styles['title']}`}>{t('brand.portal')}</h1>
        <p className={styles['subtitle']}>
          {t('login.subtitle')}
        </p>
      </header>

      <form className={styles['form']} onSubmit={handleSubmit} noValidate>
        <DocumentoInput
          value={raw}
          onChange={(r, c) => {
            setRaw(r);
            setCanonical(c);
          }}
          autoFocus
        />

        <PasswordInput
          label={t('field.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />

        {auth.error && (
          <div className="ns-alert" role="alert">
            <IconAlert size={18} />
            {auth.error}
          </div>
        )}

        <button
          className={`ns-cta ${styles['submit']}`}
          type="submit"
          disabled={submitting || canonical.length === 0 || password.length === 0}
        >
          {submitting ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <footer className={styles['footer']}>
        <p className={styles['help']}>{t('login.helpTitle')}</p>
        <p className={styles['help-detail']}>{t('login.helpBody')}</p>
      </footer>
    </main>
  );
}
